import { FC, useEffect, useRef, useState } from 'react';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import IALogo from '../../../../../assets/img/IA.svg';
import { BvIcon } from 'bevi-icon';

type TextSeg = { text: string; style?: 'bold' | 'cyan' | 'note' };

const StreamingText: FC<{ segments: TextSeg[]; speed?: number; onComplete?: () => void; instant?: boolean }> = ({
    segments, speed = 22, onComplete, instant = false,
}) => {
    const totalChars = segments.reduce((s, seg) => s + seg.text.length, 0);
    const [count, setCount] = useState(() => instant ? totalChars : 0);

    useEffect(() => {
        if (instant) { onComplete?.(); return; }
        let called = false;
        const iv = setInterval(() => {
            setCount(prev => {
                const next = Math.min(prev + 3, totalChars);
                if (next >= totalChars && !called) {
                    called = true;
                    clearInterval(iv);
                    setTimeout(() => onComplete?.(), 0);
                }
                return next;
            });
        }, speed);
        return () => clearInterval(iv);
    }, [segments, speed]);

    let rem = count;
    const nodes = segments.map((seg, i) => {
        if (rem <= 0) return null;
        const visible = seg.text.slice(0, rem);
        rem -= seg.text.length;
        if (!visible) return null;
        const renderWithBreaks = (t: string, wrapper?: (c: React.ReactNode) => React.ReactNode) => {
            const parts = t.split('\n');
            const content = parts.map((p, j) => (
                <span key={j}>{p}{j < parts.length - 1 && <br />}</span>
            ));
            return wrapper ? wrapper(content) : content;
        };
        if (seg.style === 'bold') return <strong key={i}>{renderWithBreaks(visible)}</strong>;
        if (seg.style === 'cyan') return <strong key={i} style={{ color: '#19c9dd' }}>{renderWithBreaks(visible)}</strong>;
        if (seg.style === 'note') return <span key={i} className="sim-intro-message__note">{renderWithBreaks(visible)}</span>;
        return <span key={i}>{renderWithBreaks(visible)}</span>;
    });

    return <>{nodes}</>;
};

interface Step2LoadingProps {
    onDone: () => void;
    onBack: () => void;
    type?: 'form' | 'simulate';
}

const POLL_INTERVAL_MS = 10_000;
const INITIAL_DELAY_MS = 2_000;
const MAX_RETRIES_FORM = 120; // 20min
const MAX_RETRIES_SIMULATE = 30; // 5min

const STATUS_MESSAGES_FORM = [
    'Analisando as informações do cliente',
    'Validando os dados informados com roteiro operacional dos bancos',
    'Consultando instituições financeiras que possuem API, Soma, C6, Crefaz',
];

const STATUS_MESSAGES_SIMULATE = [
    'Estamos consultando as instituições financeiras parceiras',
    'Quase pronto!',
    'As simulações serão exibidas em instantes',
];

const Step2Loading: FC<Step2LoadingProps> = ({ onDone, onBack, type = 'simulate' }) => {
    const { financeiras, resultados, handleScreeningPoll, simulateError } = useSimulationBank();
    const [statusIdx, setStatusIdx] = useState(0);
    const [error, setError] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [minTimeElapsed, setMinTimeElapsed] = useState(false);

    const STATUS_MESSAGES = type === 'form' ? STATUS_MESSAGES_FORM : STATUS_MESSAGES_SIMULATE;

    const pollRef = useRef(handleScreeningPoll);
    useEffect(() => { pollRef.current = handleScreeningPoll; }, [handleScreeningPoll]);

    const onDoneRef = useRef(onDone);
    useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

    // Congelado no valor do mount: a seleção de financeiras já foi decidida (e enviada
    // no payload do /simulate) antes desta tela aparecer. O poll de screening em background
    // (efeito abaixo) roda mesmo em type="simulate" e pode alterar `financeiras.selecionada`
    // depois do mount — se esse total fosse recalculado reativamente, o alvo da comparação
    // em "resultados.length >= selecionadasLenRef.current" mudaria no meio do caminho e o
    // spinner nunca fecharia, mesmo com a resposta do /simulate já tendo chegado.
    const selecionadas = financeiras.filter((f) => f.selecionada);
    const selecionadasLenRef = useRef(selecionadas.length);

    useEffect(() => {
        const minTimer = setTimeout(() => setMinTimeElapsed(true), 4500);

        let rotateInterval: ReturnType<typeof setInterval>;
        const rotationTimer = setTimeout(() => {
            rotateInterval = setInterval(() => {
                setStatusIdx((prev) => (prev + 1) % STATUS_MESSAGES.length);
            }, 1000);
        }, 2000);

        return () => {
            clearTimeout(minTimer);
            clearTimeout(rotationTimer);
            if (rotateInterval) clearInterval(rotateInterval);
        };
    }, []);

    useEffect(() => {
        if (!minTimeElapsed) return;

        if (type === 'form' && (financeiras.length > 0 || resultados.length > 0)) {
            onDoneRef.current();
        } else if (type === 'simulate' && resultados.length >= selecionadasLenRef.current) {
            onDoneRef.current();
        }
    }, [financeiras.length, resultados.length, minTimeElapsed, type]);

    useEffect(() => {
        if (type === 'simulate' && simulateError && minTimeElapsed) {
            setError(true);
        }
    }, [type, simulateError, minTimeElapsed]);

    // Loop de polling
    useEffect(() => {
        let cancelled = false;
        let retriesLeft = type === 'simulate' ? MAX_RETRIES_SIMULATE : MAX_RETRIES_FORM;
        let timer: ReturnType<typeof setTimeout>;

        const poll = async () => {
            try {
                const { done, count } = await pollRef.current();
                if (cancelled) return;

                if (done) {
                    if (count === 0) setNoResults(true);
                    return;
                }

                retriesLeft--;
                if (retriesLeft <= 0) {
                    setError(true);
                    return;
                }

                timer = setTimeout(poll, POLL_INTERVAL_MS);
            } catch {
                if (!cancelled) setError(true);
            }
        };

        timer = setTimeout(poll, INITIAL_DELAY_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, []);

    if (error) {
        return (
            <div className="step-loading step-loading--error">
                <div className="step-loading__icon-error">
                    <BvIcon name="cancel" style={{ width: 100, height: 100 , color: '#7779BB'}} />
                </div>
                <p className="step-loading__title">Tempo esgotado</p>
                <p className="step-loading__sub" style={{color: '#7779BB', fontSize:'20px'}}>Não foi possível obter resposta das financeiras.</p>
                <button className="step-loading__back-btn" onClick={onBack}>Voltar e tentar novamente</button>
            </div>
        );
    }

    if (noResults) {
        return (
            <div className="step-loading step-loading--error">
                <div className="step-loading__icon-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={40} height={40}>
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4m0 4h.01" />
                    </svg>
                </div>
                <p className="step-loading__title">Nenhuma financeira encontrada</p>
                <p className="step-loading__sub">Não há financeiras com integração disponível para este perfil no momento.</p>
                <button className="step-loading__back-btn" onClick={onBack}>Voltar</button>
            </div>
        );
    }

    return (
        <div className="step-loading">
            <div className="step-loading__logo">
                <img src={IALogo} alt="IA" />
            </div>

            <p className="step-loading__title">Estamos processando<br />sua simulação!</p>
            <p className="step-loading__status" key={statusIdx}>
                <StreamingText
                    segments={[{ text: STATUS_MESSAGES[statusIdx] }]}
                    speed={30}
                />
            </p>

            <div className="step-loading__dots">
                <span /><span /><span />
            </div>

        </div>
    );
};

export default Step2Loading;
